import { useMemo } from 'react';

/**
 * Generic hook for calculating compliance trends between current and previous periods
 * Useful for any compliance dashboard that compares current vs previous metrics
 * 
 * @param getPreviousMetric Optional. If not provided, uses getCurrentMetric for both periods
 */
export interface ComplianceTrendData<T> {
  current: T[];
  previous: T[];
  isLoading: boolean;
  isLoadingPrevious: boolean;
}

export interface ComplianceTrendMetrics {
  currentValue: number;
  previousValue: number;
  absoluteChange: number;
  percentageChange: number;
  trend: number; // percentage change (-100 to +100)
}

export const useComplianceTrends = <T>(
  data: ComplianceTrendData<T>,
  getCurrentMetric: (items: T[]) => number,
  getPreviousMetric?: (items: T[]) => number
): ComplianceTrendMetrics => {
  
  return useMemo(() => {
    const currentValue = data.current ? getCurrentMetric(data.current) : 0;
    // If getPreviousMetric is not provided, use getCurrentMetric for both periods (future edge case)
    const previousValue = data.previous ? (getPreviousMetric || getCurrentMetric)(data.previous) : 0;
    
    const absoluteChange = currentValue - previousValue;
    
    // Handle trend calculation with edge cases
    let trend = 0;
    if (previousValue === 0) {
      // If previous was 0, show 100% if current > 0, else 0%
      trend = currentValue > 0 ? 100 : 0;
    } else {
      trend = (absoluteChange / previousValue) * 100;
    }
    
    const percentageChange = previousValue > 0 
      ? ((currentValue - previousValue) / previousValue) * 100 
      : (currentValue > 0 ? 100 : 0);

    return {
      currentValue,
      previousValue,
      absoluteChange,
      percentageChange,
      trend
    };
  }, [data.current, data.previous, getCurrentMetric, getPreviousMetric]);
};

/**
 * Hook for calculating multiple compliance metrics at once
 * Useful when you have several KPIs to track (like in StructureChecks)
 */
export const useMultipleComplianceTrends = <T>(
  data: ComplianceTrendData<T>,
  metrics: Array<{
    key: string;
    getCurrentMetric: (items: T[]) => number;
    getPreviousMetric?: (items: T[]) => number;
  }>
): Record<string, ComplianceTrendMetrics> => {
  
  return useMemo(() => {
    const result: Record<string, ComplianceTrendMetrics> = {};
    
    metrics.forEach(({ key, getCurrentMetric, getPreviousMetric }) => {
      result[key] = {
        currentValue: data.current ? getCurrentMetric(data.current) : 0,
        previousValue: data.previous ? (getPreviousMetric || getCurrentMetric)(data.previous) : 0,
        absoluteChange: 0,
        percentageChange: 0,
        trend: 0
      };
      
      const current = result[key].currentValue;
      const previous = result[key].previousValue;
      
      result[key].absoluteChange = current - previous;
      
      if (previous === 0) {
        result[key].trend = current > 0 ? 100 : 0;
      } else {
        result[key].trend = (result[key].absoluteChange / previous) * 100;
      }
      
      result[key].percentageChange = previous > 0 
        ? ((current - previous) / previous) * 100 
        : (current > 0 ? 100 : 0);
    });
    
    return result;
  }, [data.current, data.previous, metrics]);
};
